<template>
  <form @submit.prevent="handleSubmit" class="space-y-4">
    <!-- Email Field -->
    <div class="space-y-1.5 text-left">
      <label
        for="login-email"
        class="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider"
      >
        Email Address
      </label>
      <div
        class="relative flex items-center rounded-xl border border-slate-200 dark:border-slate-700/80 bg-slate-50/70 dark:bg-slate-800/50 focus-within:bg-white dark:focus-within:bg-slate-800 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 dark:focus-within:border-indigo-400 transition-all duration-200"
      >
        <span class="pl-3.5 pr-2 text-slate-400 dark:text-slate-500 flex items-center pointer-events-none">
          <Mail class="w-4 h-4" />
        </span>
        <input
          id="login-email"
          type="email"
          v-model.trim="email"
          autocomplete="email"
          required
          class="w-full py-3 pr-3.5 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none"
          placeholder="admin@school.com"
        />
      </div>
    </div>

    <!-- Password Field -->
    <div class="space-y-1.5 text-left">
      <div class="flex items-center justify-between">
        <label
          for="login-password"
          class="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider"
        >
          Password
        </label>
        <RouterLink
          to="/forgot-password"
          class="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
        >
          Forgot password?
        </RouterLink>
      </div>
      <div
        class="relative flex items-center rounded-xl border border-slate-200 dark:border-slate-700/80 bg-slate-50/70 dark:bg-slate-800/50 focus-within:bg-white dark:focus-within:bg-slate-800 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 dark:focus-within:border-indigo-400 transition-all duration-200"
      >
        <span class="pl-3.5 pr-2 text-slate-400 dark:text-slate-500 flex items-center pointer-events-none">
          <Lock class="w-4 h-4" />
        </span>
        <input
          id="login-password"
          :type="showPassword ? 'text' : 'password'"
          v-model="password"
          autocomplete="current-password"
          required
          class="w-full py-3 pr-10 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none"
          placeholder="••••••••••••"
        />
        <button
          type="button"
          @click="showPassword = !showPassword"
          class="absolute right-3 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors rounded-lg focus:outline-none"
          :title="showPassword ? 'Hide password' : 'Show password'"
        >
          <EyeOff v-if="showPassword" class="w-4 h-4" />
          <Eye v-else class="w-4 h-4" />
        </button>
      </div>
    </div>

    <!-- Error Alert -->
    <transition name="fade">
      <div
        v-if="error"
        class="flex items-start gap-2.5 p-3 rounded-xl text-xs sm:text-sm bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 text-rose-700 dark:text-rose-300 text-left animate-shake"
      >
        <AlertCircle class="w-4 h-4 mt-0.5 flex-shrink-0 text-rose-500" />
        <span class="flex-1 leading-snug">{{ error }}</span>
      </div>
    </transition>

    <!-- Remember Me Checkbox -->
    <div class="flex items-center text-left pt-1">
      <label class="flex items-center gap-2 cursor-pointer select-none group">
        <input
          type="checkbox"
          v-model="rememberMe"
          class="w-4 h-4 rounded text-indigo-600 border-slate-300 dark:border-slate-700 focus:ring-indigo-500 dark:focus:ring-offset-slate-900 bg-white dark:bg-slate-800 transition-colors cursor-pointer"
        />
        <span class="text-xs text-slate-600 dark:text-slate-400 group-hover:text-slate-800 dark:group-hover:text-slate-200 transition-colors">
          Remember this device for 30 days
        </span>
      </label>
    </div>

    <!-- Submit Button -->
    <button
      type="submit"
      :disabled="isLoading || !email || !password"
      class="w-full mt-2 bg-gradient-to-r from-indigo-600 via-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-semibold py-3 px-4 rounded-xl active:scale-[0.99] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 border border-transparent"
    >
      <Loader2 v-if="isLoading" class="w-4 h-4 animate-spin" />
      <LogIn v-else class="w-4 h-4" />
      <span>{{ isLoading ? "Authenticating..." : "Sign In with Password" }}</span>
    </button>
  </form>
</template>

<script setup>
import { ref } from "vue";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  LogIn,
  Loader2,
  AlertCircle,
} from "lucide-vue-next";

defineProps({
  error: {
    type: String,
    default: "",
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["submit", "update:error"]);

const email = ref("");
const password = ref("");
const showPassword = ref(false);
const rememberMe = ref(false);

const handleSubmit = () => {
  if (!email.value || !password.value) {
    emit("update:error", "Please fill in both email and password.");
    return;
  }

  emit("submit", {
    email: email.value,
    password: password.value,
    rememberMe: rememberMe.value,
  });
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
}
.animate-shake {
  animation: shake 0.3s ease-in-out;
}
</style>
